import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Accordion,
    Alert,
    Box,
    Button,
    Flex,
    Heading,
    Spinner,
    Stack,
    Text,
} from '@chakra-ui/react';
import BuffettRatingCard from '../components/BuffettRatingCard';
import { getRatingHistory } from '../api';

const formatDate = (value) => {
    if (!value) return 'Unknown date';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const scoreColor = (value) => {
    const rating = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(rating)) return 'gray.600';
    if (rating >= 7) return 'green.600';
    if (rating >= 4) return 'yellow.600';
    return 'red.600';
};

const ratingsInitialState = {
    ratings: [],
    status: 'idle',
    error: null,
};

function ratingsReducer(state, action) {
    switch (action.type) {
        case 'start':
            return { ...state, status: 'loading', error: null };
        case 'success':
            return { ratings: action.payload, status: 'success', error: null };
        case 'error':
            return { ...state, status: 'error', error: action.payload };
        default:
            return state;
    }
}

function Ratings() {
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const [{ ratings, status, error }, dispatchRatings] = useReducer(ratingsReducer, ratingsInitialState);
    const loading = status === 'loading';

    const fetchRatings = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }
        dispatchRatings({ type: 'start' });
        try {
            const token = await getAccessTokenSilently();
            const data = await getRatingHistory(token);
            dispatchRatings({ type: 'success', payload: data?.ratings || [] });
        } catch (err) {
            console.error('Failed to load rating history', err);
            const message = err?.body?.error || err?.message || 'Unable to load rating history.';
            dispatchRatings({ type: 'error', payload: message });
        }
    }, [getAccessTokenSilently, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchRatings();
        }
    }, [fetchRatings, isAuthenticated]);

    const content = useMemo(() => {
        if (isAuthLoading) {
            return (
                <Flex align="center" justify="center" py={12}>
                    <Spinner color="teal.500" size="lg" />
                </Flex>
            );
        }

        if (!isAuthenticated) {
            return (
                <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" boxShadow="sm">
                    <Stack gap={3}>
                        <Heading size="md">Sign in to view your ratings</Heading>
                        <Text color="gray.600">
                            Your Buffett-style AI ratings are tied to your account. Log in to revisit prior analyses.
                        </Text>
                        <Button colorPalette="teal" onClick={() => loginWithRedirect()}>
                            Log in with Auth0
                        </Button>
                    </Stack>
                </Box>
            );
        }

        if (!ratings.length && !loading) {
            return (
                <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" boxShadow="sm">
                    <Heading size="md" mb={2}>No ratings yet</Heading>
                    <Text color="gray.600">
                        Generate your first Buffett Rating from any stock analysis page to see it listed here.
                    </Text>
                </Box>
            );
        }

        return (
            <Accordion.Root type="single" collapsible borderWidth="1px" borderRadius="lg" overflow="hidden">
                {ratings.map((entry) => (
                    <Accordion.Item key={entry.id} value={String(entry.id)}>
                        <Accordion.ItemTrigger p={4} bg="gray.50" borderBottomWidth="1px">
                            <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={3} flexWrap="wrap">
                                <Text fontWeight="bold" fontSize="lg">
                                    {entry.symbol}
                                </Text>
                                <Flex align="baseline" gap={1} color={scoreColor(entry.rating)}>
                                    <Text fontSize="2xl" fontWeight="bold">
                                        {entry.rating?.toFixed ? entry.rating.toFixed(1) : entry.rating}
                                    </Text>
                                    <Text fontSize="sm">/10</Text>
                                </Flex>
                                <Text fontSize="sm" color="gray.600">
                                    {formatDate(entry.created_at)}
                                </Text>
                            </Flex>
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent bg="white">
                            <Box p={4}>
                                <BuffettRatingCard rating={entry} />
                            </Box>
                        </Accordion.ItemContent>
                    </Accordion.Item>
                ))}
            </Accordion.Root>
        );
    }, [isAuthLoading, isAuthenticated, ratings, loading, loginWithRedirect]);

    return (
        <Box maxW="960px" mx="auto" py={8} px={{ base: 4, md: 0 }}>
            <Stack gap={6}>
                <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" flexWrap="wrap" gap={3}>
                    <Box>
                        <Heading size="lg">My AI Ratings</Heading>
                        <Text color="gray.600">Every Buffett-style score you have generated so far.</Text>
                    </Box>
                    {isAuthenticated && (
                        <Button
                            onClick={fetchRatings}
                            isLoading={loading}
                            variant="outline"
                            colorPalette="teal"
                        >
                            Refresh
                        </Button>
                    )}
                </Flex>

                {error && (
                    <Alert.Root status="error">
                        <Alert.Indicator />
                        <Alert.Content>{error}</Alert.Content>
                    </Alert.Root>
                )}

                {loading && ratings.length === 0 && (
                    <Flex align="center" justify="center" py={12}>
                        <Spinner color="teal.500" size="lg" />
                    </Flex>
                )}

                {content}
            </Stack>
        </Box>
    );
}

export default Ratings;
